import { useApolloClient } from '@apollo/client';
import { entries, fromEntries } from '@hover/utilities';
import { useCallback } from 'react';

import { queries } from '../requests';

/**
 * Refetch all queries that we use to store and detect user session information.
 * This should be run after sign up or any other mutations that affect user
 * session state without explicitly updating the cache that these queries read
 * from.
 *
 * ⚠️ Note: the asynchronous function returned by this hook will not throw if
 * either of the requests fail, `null` will be returned instead of the query
 * results in the returned query mapping
 *
 * @remarks It's also possible to accomplish this via Apollo Client's
 * `refetchQueries` feature, but that will only refetch said queries if they
 * have been requested/rendered so it's safer to always explicitly make these
 * requests. We're not using the generated `*Lazy` hooks so that we can expose a
 * `Promise` API to consumers that need to wait for these requests.
 *
 * @returns an object with the individual query results mapped as properties
 */
export const useRefreshSession = () => {
  const client = useApolloClient();

  return useCallback(async () => {
    const [detectUserSession, profile] = await Promise.allSettled([
      queries.detectUserSession(client, {}),
      queries.profile(client, {}),
    ]);

    return fromEntries(
      entries({
        detectUserSession,
        profile,
      }).map(([queryName, result]) => [
        queryName,
        result.status === 'fulfilled' ? result.value : null,
      ]),
    );
  }, [client]);
};
